import type { Pattern } from '../pipeline/pipeline';
import type { PlateLayout } from '../pipeline/estimate';

/**
 * Forme JSON-safe d'un `Pattern` pour stockage IndexedDB / Firestore plus tard.
 *
 * Particularité : `Map<string, number>` n'est pas JSON-natif → on convertit en
 * objet `Record<string, number>`. Le `stockCoverage` est dérivable de `counts`
 * + inventaire, donc on ne le persiste pas (recalculé à la volée).
 */
export type SerializedPattern = {
  width: number;
  height: number;
  cells: number[];
  paletteColorIds: string[];
  counts: Record<string, number>;
  layout: PlateLayout;
  physicalSize: { widthCm: number; heightCm: number };
  estimatedTimeMinutes: number;
};

export function serializePattern(pattern: Pattern): SerializedPattern {
  // ATTENTION : on reconstruit chaque sous-objet en littéral frais (et non
  // `layout: pattern.layout`) car le pattern peut être un Proxy Svelte 5
  // (`$state`). IndexedDB rejette les Proxies avec un `DataCloneError`.
  return {
    width: pattern.width,
    height: pattern.height,
    cells: [...pattern.cells],
    paletteColorIds: [...pattern.paletteColorIds],
    counts: Object.fromEntries(pattern.counts),
    layout: {
      plateWidth: pattern.layout.plateWidth,
      plateHeight: pattern.layout.plateHeight,
      cols: pattern.layout.cols,
      rows: pattern.layout.rows,
    },
    physicalSize: {
      widthCm: pattern.physicalSize.widthCm,
      heightCm: pattern.physicalSize.heightCm,
    },
    estimatedTimeMinutes: pattern.estimatedTimeMinutes,
  };
}

export function deserializePattern(data: SerializedPattern): Pattern {
  return {
    width: data.width,
    height: data.height,
    cells: data.cells,
    paletteColorIds: data.paletteColorIds,
    counts: new Map(Object.entries(data.counts)),
    layout: data.layout,
    physicalSize: data.physicalSize,
    estimatedTimeMinutes: data.estimatedTimeMinutes,
    // `stockCoverage` n'est pas persisté : recalculable via computeStockCoverage
    // côté pipeline avec l'inventaire utilisateur courant.
  };
}
